/**
 * ReclaimGroupTiles — a small home-page tile row linking to the
 * three properties in the Soulumo Group family: Soulumo, Soulumo
 * Foundations and One Million Lights.
 *
 * Placement: below the WindowPicker on the Reclaim home page. Each
 * tile is a soft dark card with a warm-gold accent and a one-line
 * subtitle so the visitor understands what sits behind each door.
 *
 * All three tiles are tappable regardless of auth state — Soulumo &
 * Foundations still resolve at the same paths for signed-in users;
 * OML is public.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const TILES = [
  {
    id: "soulumo",
    label: "Soulumo",
    sub: "Signature audio installations",
    // ?brand=soulumo forces the hostname-router into Soulumo mode
    // even from within the Reclaim host.
    href: "/?brand=soulumo",
    accent: "#e0a97a",   // warm gold
    orb: "linear-gradient(135deg, #e0a97a 0%, #b17849 100%)",
    testid: "reclaim-home-tile-soulumo",
  },
  {
    id: "foundations",
    label: "Foundations",
    sub: "The gift · free membership",
    href: "/foundations",
    accent: "#e5c891",   // soft gold
    orb: "linear-gradient(135deg, #f0d5a5 0%, #c6a06a 100%)",
    testid: "reclaim-home-tile-foundations",
  },
  {
    id: "oml",
    label: "One Million Lights",
    sub: "Place a light",
    href: "/?brand=oml",
    accent: "#f2c56a",   // gold-forward
    orb: "linear-gradient(135deg, #ffe2a1 0%, #d99a4a 60%, #7a4a1c 100%)",
    testid: "reclaim-home-tile-oml",
  },
];

export default function ReclaimGroupTiles() {
  const nav = useNavigate();

  const open = (tile) => {
    // Cross-brand links use a full-page nav (`window.location`) so
    // the hostname-router resolves cleanly from a cold state.
    if (tile.href.startsWith("/?brand=")) {
      window.location.href = tile.href;
    } else {
      nav(tile.href);
    }
  };

  return (
    <section data-testid="reclaim-home-group-tiles" className="pt-1">
      <div
        className="mb-3 text-[0.62rem] uppercase"
        style={{
          color: "rgba(180,160,120,0.72)",
          fontFamily: '"Manrope", sans-serif',
          letterSpacing: "0.32em",
        }}
      >
        Soulumo Group
      </div>

      <div className="grid grid-cols-3 gap-3">
        {TILES.map((t, i) => (
          <motion.button
            key={t.id}
            type="button"
            onClick={() => open(t)}
            data-testid={t.testid}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * i, duration: 0.5, ease: "easeOut" }}
            whileTap={{ scale: 0.97 }}
            className="relative rounded-2xl overflow-hidden text-left px-3 py-4"
            style={{
              background: "linear-gradient(180deg, rgba(24,20,15,0.9) 0%, rgba(10,8,6,0.95) 100%)",
              border: `1px solid ${t.accent}66`,
              boxShadow: `0 8px 20px rgba(0,0,0,0.35), 0 0 22px ${t.accent}22`,
              minHeight: 112,
            }}
          >
            {/* Ambient gold halo in the top-right corner */}
            <div
              aria-hidden
              className="absolute pointer-events-none"
              style={{
                top: -16, right: -16, width: 60, height: 60, borderRadius: "50%",
                background: `radial-gradient(circle, ${t.accent}55 0%, transparent 70%)`,
                filter: "blur(6px)",
              }}
            />
            <div
              aria-hidden
              className="w-6 h-6 rounded-full mb-2"
              style={{
                background: t.orb,
                boxShadow: `0 0 12px ${t.accent}80`,
              }}
            />
            <div
              className="leading-tight"
              style={{
                fontFamily: '"Playfair Display", "Cormorant Garamond", serif',
                fontSize: "0.95rem",
                color: "#f5ecd9",
                fontStyle: "italic",
                fontWeight: 400,
              }}
            >
              {t.label}
            </div>
            <div
              className="mt-1 leading-snug"
              style={{
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.62rem",
                color: "rgba(245,236,217,0.6)",
                letterSpacing: "0.02em",
              }}
            >
              {t.sub}
            </div>
          </motion.button>
        ))}
      </div>
    </section>
  );
}
